import { computed, type ComputedRef, type Ref } from 'vue'

export interface CampaignThemeStyles {
    /** Style binding for the module's outer border (e.g. listing card frame, PDP box). */
    borderStyle: ComputedRef<Record<string, string> | undefined>
    /** Style binding for a header band background (e.g. slider header, PDP box header). */
    headerStyle: ComputedRef<Record<string, string> | undefined>
    /** Style binding for a filled bar (availability bar, discount bar). */
    barStyle: ComputedRef<Record<string, string> | undefined>
    /** Style binding for a badge background. */
    badgeStyle: ComputedRef<Record<string, string> | undefined>
}

/**
 * Resolves an optional campaign/promotion accent color (hex string) into style bindings for the
 * surfaces that need to react to it: border, header background, filled bars and badges.
 *
 * Text on the accent color is always white — callers combine `headerStyle`/`badgeStyle` with a
 * `text-white` (or equivalent) utility class rather than relying on a color token.
 *
 * Without a color, every binding resolves to `undefined` so callers keep their existing
 * Tailwind/DaisyUI default classes (`bg-neutral-focus`, `text-neutral-content`, `bg-sale`, …)
 * untouched — the color is an override, never a replacement for those defaults.
 */
export function useCampaignTheme (color: Ref<string | null | undefined>): CampaignThemeStyles {
    const hasColor = computed(() => color.value != null && color.value !== '')

    const borderStyle = computed(() => (hasColor.value ? { borderColor: color.value as string } : undefined))
    const headerStyle = computed(() => (hasColor.value ? { backgroundColor: color.value as string, color: '#fff' } : undefined))
    const barStyle = computed(() => (hasColor.value ? { backgroundColor: color.value as string } : undefined))
    const badgeStyle = computed(() => (hasColor.value ? { backgroundColor: color.value as string, color: '#fff' } : undefined))

    return {
        borderStyle,
        headerStyle,
        barStyle,
        badgeStyle
    }
}
